import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MapPicker from "@/components/MapPicker";
import IncidentForm from "@/components/IncidentForm";
import { ImageSlot, AccidentBatchUpload, RoadtaxUpload } from "@/components/ImageUpload";
import api, { formatApiError, downloadInvoice } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, FilePdf, FileDashed } from "@phosphor-icons/react";

const INSURERS = [
  "Etiqa", "Allianz", "AXA Affin", "MSIG", "Takaful Malaysia", "Zurich",
  "Tokio Marine", "Liberty Insurance", "RHB Insurance", "AIA", "Great Eastern", "Other / None",
];

const PARKING_LOCATIONS = [
  "PSS Yard - Klang", "PSS Yard - Shah Alam", "PSS Yard - Kuala Lumpur",
  "Customer Residence", "Police Station Compound", "Panel Workshop", "Other",
];

const OUTSOURCE_SERVICES = [
  "Accident Recovery Car Towing",
  "Breakdown Recovery Car Towing",
  "Accident Recovery Motorcycle Towing",
  "Breakdown Recovery Motorcycle Towing",
  "Battery Rescue (Jumper + Inspection Free)",
  "Battery Rescue (Battery Exchange 1 to 1 + Inspection Free)",
  "Fuel Rescue",
];

function Section({ title, hint, children, testid }) {
  return (
    <section className="space-y-5 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid={testid}>
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
        {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const EMPTY_CUSTOMER = { name: "", nric: "", phone: "", email: "", insurance: "" };

export default function OutsourceForm({ user, staff, onDone, draft = null }) {
  const [settings, setSettings] = useState(null);
  const [runnerId, setRunnerId] = useState(draft?.runner_id || "");
  const [driverId, setDriverId] = useState(draft?.driver_id || (user.role === "driver" ? user.id : ""));
  const [from, setFrom] = useState(draft?.from_address ? { lat: draft.from_lat, lng: draft.from_lng, label: draft.from_address } : null);
  const [to, setTo] = useState(draft?.to_address ? { lat: draft.to_lat, lng: draft.to_lng, label: draft.to_address } : null);
  const [distanceKm, setDistanceKm] = useState(draft?.distance_km || 0);
  const [tolls, setTolls] = useState(draft?.tolls ? String(draft.tolls) : "");
  const [vehicleType, setVehicleType] = useState(draft?.vehicle_type || "car");
  const [serviceType, setServiceType] = useState(draft?.service_type || "");
  const [discount, setDiscount] = useState(draft?.discount_amount ? String(draft.discount_amount) : "");
  const [customer, setCustomer] = useState(draft?.customer?.name ? { ...EMPTY_CUSTOMER, ...draft.customer } : EMPTY_CUSTOMER);
  const [insuranceOther, setInsuranceOther] = useState("");
  const [insurers, setInsurers] = useState(INSURERS);
  const [newInsurer, setNewInsurer] = useState("");
  const [roadtaxValid, setRoadtaxValid] = useState(draft?.roadtax_valid ?? true);
  const [roadtaxExpiry, setRoadtaxExpiry] = useState(draft?.roadtax_expiry || "");
  const [parkingFee, setParkingFee] = useState(draft?.parking_fee_per_day ? String(draft.parking_fee_per_day) : "");
  const [parkingDays, setParkingDays] = useState(draft?.parking_days || 1);
  const [parkingLocation, setParkingLocation] = useState(draft?.location_parking || "");
  const [extraTyres, setExtraTyres] = useState(draft?.extra_tyres ? String(draft.extra_tyres) : "");
  const [vehicle, setVehicle] = useState({ front: null, back: null, left: null, right: null });
  const [accident, setAccident] = useState([]);
  const [roadtaxPhoto, setRoadtaxPhoto] = useState(null);
  const [roadtaxUnable, setRoadtaxUnable] = useState(false);
  const [nricFront, setNricFront] = useState(null);
  const [nricBack, setNricBack] = useState(null);
  const [incident, setIncident] = useState(draft?.incident || {});
  const [notes, setNotes] = useState(draft?.notes || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/insurance-providers").then((r) => {
      if (r.data?.length) setInsurers(r.data.map((p) => p.name));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const ins = draft?.customer?.insurance;
    if (ins && insurers.length && !insurers.includes(ins)) {
      setCustomer((c) => ({ ...c, insurance: "Other / None" }));
      setInsuranceOther(ins);
    }
  }, [draft, insurers]);

  const pricing = useMemo(() => {
    if (!settings || !distanceKm || distanceKm <= 0) return null;
    const hour = new Date().getHours();
    const isAm = hour >= 8 && hour < 20;
    const basePrice = Number(
      (vehicleType === "motorcycle"
        ? (isAm ? settings.base_moto_am : settings.base_moto_pm)
        : (isAm ? settings.base_car_am : settings.base_car_pm)) ?? settings.base_price ?? 0
    );
    const extraKm = Math.max(0, distanceKm - (settings.base_km ?? 10));
    const extraCharge = extraKm * (settings.extra_rate ?? 0);
    const parkingTotal = (parseFloat(parkingFee) || 0) * (parkingDays || 1);
    const extraTyresAmt = parseFloat(extraTyres) || 0;
    const subtotal = basePrice + extraCharge;
    const total = subtotal + (parseFloat(tolls) || 0) + parkingTotal + extraTyresAmt;
    return {
      extraKm, extraCharge, parkingTotal, extraTyresAmt, subtotal, total, basePrice,
      period: isAm ? "AM" : "PM",
      runnerComm: Number(settings.runner_commission_flat ?? 0),
      driverComm: Number(settings.driver_commission_flat ?? 0),
    };
  }, [settings, distanceKm, tolls, parkingFee, parkingDays, vehicleType, extraTyres]);

  const discountAmt = parseFloat(discount) || 0;
  const finalTotal = pricing ? Math.max(0, pricing.total - discountAmt) : null;

  const handleMapChange = (field, val) => {
    if (field === "from") setFrom(val);
    else if (field === "to") setTo(val);
    else if (field === "distance") setDistanceKm(val);
  };

  const addInsurer = async () => {
    if (!newInsurer.trim()) return;
    try {
      await api.post("/insurance-providers", { name: newInsurer.trim() });
      toast.success("Insurance provider registered");
      setNewInsurer("");
      const r = await api.get("/insurance-providers");
      if (r.data?.length) setInsurers(r.data.map((p) => p.name));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const validate = () => {
    if (!driverId) return "Please select a driver";
    if (!from || !to) return "Please set both From and To locations";
    if (distanceKm <= 0) return "Waiting for distance calculation";
    if (!customer.name.trim() || !customer.nric.trim()) return "Customer name and NRIC/Passport are required";
    if (!vehicle.front || !vehicle.back || !vehicle.left || !vehicle.right) return "All 4 vehicle condition photos are required";
    if (!serviceType) return "Please select a service type";
    if (serviceType.startsWith("Accident") && accident.length < 1) return "At least 1 accident scene photo is required for accident recovery jobs";
    if (!roadtaxUnable && !roadtaxPhoto) return "Roadtax photo required (or tick unable)";
    if (!nricFront || !nricBack) return "Both NRIC front and back photos are required";
    return null;
  };

  const buildPayload = () => ({
    type: "outsource",
    runner_id: runnerId,
    driver_id: driverId,
    from_address: from?.label || "", from_lat: from?.lat, from_lng: from?.lng,
    to_address: to?.label || "", to_lat: to?.lat, to_lng: to?.lng,
    distance_km: distanceKm,
    tolls: parseFloat(tolls) || 0,
    vehicle_type: vehicleType,
    service_type: serviceType,
    discount: discountAmt,
    customer: { ...customer, insurance: customer.insurance === "Other / None" ? (insuranceOther.trim() || "Other / None") : customer.insurance },
    roadtax_valid: roadtaxValid,
    roadtax_expiry: roadtaxExpiry,
    parking_fee_per_day: parseFloat(parkingFee) || 0,
    parking_days: parkingDays,
    location_parking: parkingLocation,
    extra_tyres: parseFloat(extraTyres) || 0,
    incident,
    notes,
  });

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...buildPayload(),
        images: { ...vehicle, accident, roadtax: roadtaxPhoto, roadtax_unable: roadtaxUnable },
        nric_front: { image: nricFront, watermarked: true },
        nric_back: { image: nricBack, watermarked: true },
      };
      const { data } = await api.post("/submissions", payload);
      if (draft?.id) await api.delete(`/submissions/${draft.id}`).catch(() => {});
      setDone(data);
      toast.success(`Job ${data.job_no} submitted — invoice generated`);
      onDone?.();
    } catch (err2) {
      toast.error(formatApiError(err2));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/submissions", { ...buildPayload(), draft: true });
      if (draft?.id) await api.delete(`/submissions/${draft.id}`).catch(() => {});
      toast.success("Draft saved — find it under the Drafts tab. Photos are not stored in drafts; re-attach them when you resume.");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-sm border border-border bg-card p-12 text-center animate-fade-up" data-testid="outsource-success">
        <CheckCircle size={56} weight="fill" className="text-emerald-400" />
        <h3 className="font-display text-2xl font-bold">Job Submitted</h3>
        <p className="font-mono text-lg text-primary">{done.job_no}</p>
        <p className="text-sm text-muted-foreground">
          Invoice <span className="font-mono text-foreground">{done.invoice_no}</span> generated · Total RM {(Number(done.pricing?.total) || 0).toFixed(2)}
          {done.customer?.email ? " · emailed to customer" : ""}
        </p>
        {done.discount?.amount > 0 && (
          <p className="text-sm text-amber-400" data-testid="discount-applied-note">
            Discount of RM {Number(done.discount.amount).toFixed(2)} applied — logged as provided by {done.discount.by}.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          {done.invoice_no && (
            <Button data-testid="download-invoice-button" onClick={() => downloadInvoice(done.id, done.invoice_no)}
              className="rounded-sm bg-primary font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              <FilePdf size={16} className="mr-2" /> Download Invoice
            </Button>
          )}
          <Button data-testid="outsource-new-button" variant="outline" onClick={() => window.location.reload()}
            className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
            New Job
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="outsource-form">
      {draft && (
        <div className="flex items-center gap-3 rounded-sm border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="draft-resume-banner">
          <FileDashed size={18} className="shrink-0 text-amber-400" />
          <p className="text-xs font-semibold text-amber-300">
            Resuming draft <span className="font-mono">{draft.job_no}</span> — saved data pre-filled. Photos were not stored; please re-attach them before submitting.
          </p>
        </div>
      )}
      <Section title="Staff Allocation" hint="Assign the runner and driver for this job." testid="section-staff">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Service Type *</Label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger data-testid="service-type-select" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {OUTSOURCE_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff Name [Runner] (Optional)</Label>
            <Select value={runnerId} onValueChange={setRunnerId}>
              <SelectTrigger data-testid="runner-select" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select runner" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {(staff?.runners || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff Name [Driver] *</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="driver-select" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select driver" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {(staff?.drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="Mapping & Routing" hint="Klang Valley / Kuala Lumpur default region. Set pickup and drop-off to auto-calculate KM." testid="section-routing">
        <MapPicker from={from} to={to} distanceKm={distanceKm} onChange={handleMapChange} />
      </Section>

      <Section title="Pricing" hint="Auto-calculated from distance, vehicle type, time of day and current rates." testid="section-pricing">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vehicle Type *</Label>
            <div className="flex gap-2" data-testid="vehicle-type-toggle">
              {[["car", "Car"], ["motorcycle", "Motorcycle"]].map(([v, label]) => (
                <button key={v} type="button" data-testid={`vehicle-type-${v}`} onClick={() => setVehicleType(v)}
                  className={`rounded-sm border px-5 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                    vehicleType === v ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background text-muted-foreground hover:text-foreground"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Tolls (RM)</Label>
            <Input data-testid="tolls-input" type="number" min="0" step="0.01" value={tolls} onChange={(e) => setTolls(e.target.value)}
              placeholder="0.00" className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Discount (RM)</Label>
            <Input data-testid="discount-input" type="number" min="0" step="0.01" value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="0.00" className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
            <p className="text-[10px] text-muted-foreground">Optional. Applies instantly to the invoice — your name is logged with it for accountability.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Extra Tyres (RM)</Label>
            <Input data-testid="extra-tyres-input" type="number" min="0" step="0.01" value={extraTyres}
              onChange={(e) => setExtraTyres(e.target.value)}
              placeholder="0.00" className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
            <p className="text-[10px] text-muted-foreground">Optional. Spare/extra tyre charge — applies to breakdown and accident jobs.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px border border-border/60 bg-border/40">
          {[
            ["Base (first 10 KM)", pricing ? `RM ${(pricing.basePrice ?? 0).toFixed(2)} (${pricing.period})` : "—", "price-base"],
            ["Extra KM", pricing ? `${(pricing.extraKm ?? 0).toFixed(1)} KM = RM ${(pricing.extraCharge ?? 0).toFixed(2)}` : "—", "price-extra"],
          ].map(([label, val, tid]) => (
            <div key={tid} className="bg-background p-4" data-testid={tid}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className="mt-1 font-mono text-sm font-bold text-foreground">{val}</p>
            </div>
          ))}
        </div>
        {pricing && pricing.parkingTotal > 0 && (
          <div className="flex items-center justify-between rounded-sm border border-border bg-card px-6 py-3" data-testid="price-parking-line">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Parking ({parkingDays || 1} day{(parkingDays || 1) > 1 ? "s" : ""})</span>
            <span className="font-mono text-sm font-bold text-foreground">RM {pricing.parkingTotal.toFixed(2)}</span>
          </div>
        )}
        {pricing && pricing.extraTyresAmt > 0 && (
          <div className="flex items-center justify-between rounded-sm border border-border bg-card px-6 py-3" data-testid="price-extra-tyres-line">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Extra Tyres</span>
            <span className="font-mono text-sm font-bold text-foreground">RM {pricing.extraTyresAmt.toFixed(2)}</span>
          </div>
        )}
        {pricing && discountAmt > 0 && (
          <div className="flex items-center justify-between rounded-sm border border-border bg-card px-6 py-3" data-testid="price-discount-line">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Discount (logged under your name)</span>
            <span className="font-mono text-sm font-bold text-red-400">− RM {discountAmt.toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-center justify-between rounded-sm border border-primary/40 bg-primary/5 px-6 py-4">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Estimated Total</span>
          <span data-testid="price-total" className="font-mono text-2xl font-black text-primary">
            {finalTotal !== null ? `RM ${finalTotal.toFixed(2)}` : "—"}
          </span>
        </div>
      </Section>

      <Section title="Customer Details" testid="section-customer">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Name *</Label>
            <Input data-testid="customer-name-input" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">NRIC / Passport *</Label>
            <Input data-testid="customer-nric-input" value={customer.nric} onChange={(e) => setCustomer({ ...customer, nric: e.target.value })}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Phone</Label>
            <Input data-testid="customer-phone-input" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email (for invoice)</Label>
            <Input data-testid="customer-email-input" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Insurance Provider</Label>
            <Select value={customer.insurance} onValueChange={(v) => setCustomer({ ...customer, insurance: v })}>
              <SelectTrigger data-testid="insurance-select" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select insurer" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {insurers.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
            {customer.insurance === "Other / None" && (
              <Input data-testid="insurance-other-input" value={insuranceOther} onChange={(e) => setInsuranceOther(e.target.value)}
                placeholder="Type insurance name (optional)..."
                className="rounded-sm border-input bg-background focus-visible:ring-primary" />
            )}
            {["admin", "ceo", "coceo", "dev"].includes(user.role) && (
              <div className="flex gap-2 pt-1">
                <Input data-testid="insurance-add-input" value={newInsurer} onChange={(e) => setNewInsurer(e.target.value)}
                  placeholder="Register new insurance provider..."
                  className="rounded-sm border-input bg-background focus-visible:ring-primary" />
                <Button type="button" variant="outline" data-testid="insurance-add-button" onClick={addInsurer}
                  className="shrink-0 rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                  Register Provider
                </Button>
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title="Compliance — Roadtax" hint="Parking fee is strictly optional, regardless of roadtax validity." testid="section-compliance">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Roadtax Validity *</Label>
            <div className="flex gap-2" data-testid="roadtax-validity-toggle">
              {[true, false].map((v) => (
                <button key={String(v)} type="button" data-testid={`roadtax-${v ? "valid" : "invalid"}-button`}
                  onClick={() => setRoadtaxValid(v)}
                  className={`rounded-sm border px-6 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                    roadtaxValid === v
                      ? v ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-destructive bg-destructive/15 text-red-400"
                      : "border-input bg-background text-muted-foreground hover:text-foreground"
                  }`}>
                  {v ? "Valid" : "Invalid"}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Roadtax Expiry Date</Label>
            <Input data-testid="roadtax-expiry-input" type="date" value={roadtaxExpiry} onChange={(e) => setRoadtaxExpiry(e.target.value)}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 rounded-sm border border-input bg-card/50 p-5 md:grid-cols-3" data-testid="parking-fee-section">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Parking Fee (RM/day) — Optional</Label>
            <Input data-testid="parking-fee-input" type="number" min="0" step="0.01" value={parkingFee} onChange={(e) => setParkingFee(e.target.value)}
              placeholder="0.00" className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Days</Label>
            <Input data-testid="parking-days-input" type="number" min="1" value={parkingDays} onChange={(e) => setParkingDays(parseInt(e.target.value) || 1)}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Location Parking — Optional</Label>
            <Select value={parkingLocation} onValueChange={setParkingLocation}>
              <SelectTrigger data-testid="parking-location-select" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {PARKING_LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="Vehicle Condition" hint="All 4 angles are mandatory." testid="section-vehicle">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <ImageSlot label="Front" value={vehicle.front} onChange={(v) => setVehicle({ ...vehicle, front: v })} testid="vehicle-front-upload" />
          <ImageSlot label="Back" value={vehicle.back} onChange={(v) => setVehicle({ ...vehicle, back: v })} testid="vehicle-back-upload" />
          <ImageSlot label="Left Side" value={vehicle.left} onChange={(v) => setVehicle({ ...vehicle, left: v })} testid="vehicle-left-upload" />
          <ImageSlot label="Right Side" value={vehicle.right} onChange={(v) => setVehicle({ ...vehicle, right: v })} testid="vehicle-right-upload" />
        </div>
      </Section>

      <Section title="Accident Scene" hint="Mandatory for Accident Recovery jobs (1-10 photos). Optional for breakdown jobs." testid="section-accident">
        <AccidentBatchUpload values={accident} onChange={setAccident} />
      </Section>

      <Section title="Roadtax & NRIC" hint="NRIC photos are auto-stamped with the PSS security watermark at 45°." testid="section-docs">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <RoadtaxUpload value={roadtaxPhoto} unable={roadtaxUnable} onPhoto={setRoadtaxPhoto}
            onUnable={(v) => { setRoadtaxUnable(v); if (v) setRoadtaxPhoto(null); }} />
          <ImageSlot label="NRIC Front" value={nricFront} onChange={setNricFront} watermark testid="nric-front-upload" />
          <ImageSlot label="NRIC Back" value={nricBack} onChange={setNricBack} watermark testid="nric-back-upload" />
        </div>
      </Section>

      <Section title="Incident & Injury Report" hint="Conditional fields expand based on your answers." testid="section-incident">
        <IncidentForm value={incident} onChange={setIncident} />
      </Section>

      <Section title="Additional Notes" testid="section-notes">
        <Textarea data-testid="outsource-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional remarks for dispatch..." className="rounded-sm border-input bg-background focus-visible:ring-primary" />
      </Section>

      <div className="sticky bottom-4 z-40 flex flex-col gap-3 sm:flex-row">
        <Button data-testid="outsource-submit-button" type="submit" disabled={busy}
          className="flex-1 rounded-sm bg-primary py-7 text-sm font-black uppercase tracking-widest text-primary-foreground shadow-lg shadow-amber-500/20 transition-all hover:-translate-y-0.5 hover:bg-amber-400 hover:text-black">
          {busy ? "Processing..." : "Submit Job & Generate Invoice"}
        </Button>
        <Button data-testid="outsource-draft-button" type="button" variant="outline" disabled={busy} onClick={saveDraft}
          className="rounded-sm border-input bg-background/95 px-8 py-7 font-bold uppercase tracking-wider text-muted-foreground shadow-lg hover:bg-secondary hover:text-foreground">
          <FileDashed size={16} className="mr-2" /> Save as Draft
        </Button>
      </div>
    </form>
  );
}
